// 소비 아이템·보석 창의 표시 모델(D2c, 순수 로직). DOM은 consumePanel.ts가 그린다.
// 가격·판매 마을·보유 수·남은 시간은 게임 규칙(content/consumables·shared/gear)에서 바로 만든다 — 화면 값이 실제 판정과 어긋나지 않게.
import {
  ALCHEMIST_TOWN, ELIXIRS, ELIXIR_MAX_SEC, GEM_CAP, GEM_COLORS, GEM_COMBINE, GEM_TIERS, GEM_TIER_NAME, JEWELER_TOWN, POTIONS, SCROLLS, STACK_CAP, elixirPrice, gemCode,
  gemCombineFee, gemEffectText, gemName, gemTierOf, shopMult, shopPrice, unsocketFee,
} from '../shared/content/consumables';
import { SOCKET_CAP } from '../shared/content/items';
import { equipDef, itemName, socketsOf, type GearItem } from '../shared/gear';
import { SLOT_ORDER } from '../shared/gearLedger';
import type { GearView } from '../shared/gearView';
import { gradeColor, gradeName, SLOT_KO } from './gearModel';

const num = (v: number): string => v.toLocaleString('ko-KR');
const pct = (v: number): string => (Math.round(v * 1000) / 10) + '%';
/** 남은 초 → "29:41" */
export function mmss(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}

export interface ShopRow {
  readonly id: string;
  readonly name: string;
  readonly desc: string;
  /** 지금 가격(판매하지 않으면 null) */
  readonly price: number | null;
  readonly owned: number;
  /** 아직 안 열림(다음 마을) 안내 */
  readonly lockText: string;
  /** 1개·10개 살 수 있는지 */
  readonly can1: boolean;
  readonly can10: boolean;
}
/** 잡화상 「물약·주문서」 탭 줄 */
export function shopRows(g: Pick<GearView, 'cons'>, town: number, gold: number, locked: boolean): ShopRow[] {
  const rows: ShopRow[] = [];
  const mk = (id: string, name: string, desc: string, unlock: number): ShopRow => {
    const price = shopPrice(id, town);
    const owned = g.cons[id] ?? 0;
    const room = STACK_CAP - owned;
    return {
      id, name, desc, price, owned, lockText: price === null ? `마을${'①②③④⑤⑥'[unlock - 1]}부터 판매` : '',
      can1: !locked && price !== null && gold >= price && room >= 1,
      can10: !locked && price !== null && gold >= price * 10 && room >= 10,
    };
  };
  for (const p of POTIONS) rows.push(mk(p.id, p.name, (p.kind === 'hp' ? '최대 HP ' : '최대 MP ') + pct(p.heal) + ' 회복 · 퀵슬롯 ' + (p.kind === 'hp' ? 'R' : 'T'), p.town));
  for (const s of SCROLLS) rows.push(mk(s.id, s.name, s.desc, 1));
  return rows;
}

export interface AlchemyRow {
  readonly id: string;
  readonly name: string;
  readonly effect: string;
  readonly price: number;
  readonly owned: number;
  readonly can1: boolean;
  readonly can5: boolean;
}
/** 연금술(비약 제작) — 마을④부터. 열리기 전이면 null */
export function alchemyRows(g: Pick<GearView, 'cons'>, town: number, gold: number, locked: boolean): AlchemyRow[] | null {
  if (town < ALCHEMIST_TOWN) return null;
  const price = elixirPrice(town);
  return ELIXIRS.map((e) => {
    const owned = g.cons[e.id] ?? 0;
    return {
      id: e.id, name: e.name, effect: elixirEffect(e.stat, e.value) + ' · 30분(접속 시간)', price, owned,
      can1: !locked && gold >= price && owned + 1 <= STACK_CAP, can5: !locked && gold >= price * 5 && owned + 5 <= STACK_CAP,
    };
  });
}
function elixirEffect(stat: string, v: number): string {
  return stat === 'atkPct' ? '공격력 +' + pct(v) : stat === 'damageTaken' ? '받는 피해 −' + pct(v) : stat === 'dropRate' ? '드롭률 +' + pct(v) : 'EXP +' + pct(v);
}

export interface ElixirRow {
  readonly id: string;
  readonly fam: string;
  readonly name: string;
  readonly effect: string;
  readonly owned: number;
  /** 남은 시간(초, 0 = 꺼짐)과 문구 */
  readonly left: number;
  readonly leftText: string;
  /** 마시면 남은 시간이 상한(60분)에 막혀 늘지 않는다 */
  readonly full: boolean;
}
/** 가방 「소비」 탭 비약 줄 */
export function elixirRows(g: Pick<GearView, 'cons' | 'elix' | 'play'>): ElixirRow[] {
  return ELIXIRS.map((e, k) => {
    const left = Math.max(0, (g.elix[k] ?? 0) - g.play);
    return { id: e.id, fam: e.fam, name: e.name, effect: elixirEffect(e.stat, e.value), owned: g.cons[e.id] ?? 0, left, leftText: left > 0 ? '남은 ' + mmss(left) : '꺼짐', full: left >= ELIXIR_MAX_SEC };
  });
}
/** 가방 「소비」 탭 물약·주문서 보유 줄 */
export function carryRows(g: Pick<GearView, 'cons'>): { id: string; name: string; owned: number }[] {
  return [...POTIONS.map((p) => ({ id: p.id, name: p.name, owned: g.cons[p.id] ?? 0 })), ...SCROLLS.map((s) => ({ id: s.id, name: s.name, owned: g.cons[s.id] ?? 0 }))];
}

// ── 보석 ──
export interface GemInvRow {
  readonly code: number;
  readonly name: string;
  readonly effect: string;
  readonly color: string;
  readonly count: number;
  /** 합성(3 → 위 등급 1) 가능·수수료 */
  readonly canCombine: boolean;
  readonly combineFee: number;
  readonly top: boolean;
}
/** 가진 보석(수 > 0)만, 색 → 등급 순 */
export function gemInventory(g: Pick<GearView, 'gems'>, town: number, gold: number, locked: boolean): GemInvRow[] {
  const out: GemInvRow[] = [];
  for (let c = 0; c < GEM_COLORS.length; c++) for (let t = 1; t <= GEM_TIERS; t++) {
    const code = gemCode(c, t), n = g.gems[code] ?? 0;
    if (n <= 0) continue;
    const top = t >= GEM_TIERS, fee = top ? 0 : gemCombineFee(code, town);
    out.push({ code, name: gemName(code), effect: gemEffectText(code), color: GEM_COLORS[c].color, count: n, top, combineFee: fee, canCombine: !locked && !top && town >= JEWELER_TOWN && n >= GEM_COMBINE.need && gold >= fee });
  }
  return out;
}
export interface SocketItemRow {
  readonly uid: string;
  readonly name: string;
  readonly sub: string;
  readonly color: string;
  readonly equipped: boolean;
  /** 칸마다 보석 코드(−1 빈 칸) */
  readonly sockets: readonly number[];
}
/** 소켓이 있는 장비(장착 먼저, 그다음 가방) */
export function socketItems(g: Pick<GearView, 'items' | 'equipped'>): SocketItemRow[] {
  const eq = new Set(SLOT_ORDER.map((k) => g.equipped[k]).filter((u): u is string => !!u));
  const rows = g.items.filter((it) => SOCKET_CAP[it.grade] > 0 && !!equipDef(it.def)).map((it) => toSocketRow(it, eq.has(it.uid)));
  return rows.sort((a, b) => Number(b.equipped) - Number(a.equipped) || (a.uid < b.uid ? -1 : 1));
}
function toSocketRow(it: GearItem, equipped: boolean): SocketItemRow {
  const d = equipDef(it.def);
  return { uid: it.uid, name: itemName(it), sub: gradeName(it.grade) + ' · ' + (d ? SLOT_KO[d.slot] : '?') + ' · 소켓 ' + SOCKET_CAP[it.grade] + (equipped ? ' · 장착 중' : ''), color: gradeColor(it.grade), equipped, sockets: socketsOf(it) };
}
/** 소켓 칸 문구(빈 칸 / 보석 이름·효과) */
export function socketText(code: number): string { return code >= 0 ? gemName(code) + ' · ' + gemEffectText(code) : '빈 소켓'; }
/** 장비 카드 소켓 줄(장비함 상세) */
export function socketLine(it: GearItem): string {
  const n = SOCKET_CAP[it.grade];
  if (n === 0) return '';
  return '소켓 ' + socketsOf(it).map((c) => (c >= 0 ? gemName(c) : '빈 칸')).join(' · ');
}
/** 보석 탭 안내(상한·수수료) */
export function gemRuleText(town: number): string {
  return `같은 보석 ${GEM_COMBINE.need}개 → 한 등급 위 1개(확정, 확률 없음) · 박기 무료 · 빼기 ${num(unsocketFee(town))}골드(보석은 돌려받음) · 효과 합 상한: 공격력 +${pct(GEM_CAP.atkPct)}, 최대 HP +${pct(GEM_CAP.hpPct)}, MP 재생 +${pct(GEM_CAP.mpRegen)}`;
}
/** 보석 탭이 아직 잠김(마을③ 전) 안내. 열렸으면 '' */
export function gemLockText(town: number): string { return town >= JEWELER_TOWN ? '' : '보석 소켓·합성은 마을③ 서리 항구에서 열립니다(지역3부터 엘리트·보스가 보석을 떨어뜨립니다).'; }
export function alchemyLockText(town: number): string { return town >= ALCHEMIST_TOWN ? '' : '비약 제작은 마을④ 늪지 장터의 연금술에서 열립니다.'; }
/** 등급 이름 표(문서·테스트) */
export const GEM_GRADE_TEXT = GEM_TIER_NAME;
export { gemTierOf, shopMult };
